import { useRef, useState, useEffect } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

function useStaggerVisible(count: number, isVisible: boolean, delayBase = 100) {
  const [visibleItems, setVisibleItems] = useState<boolean[]>(Array(count).fill(false));
  useEffect(() => {
    if (!isVisible) return;
    visibleItems.forEach((_, i) => {
      setTimeout(() => {
        setVisibleItems((prev) => {
          const next = [...prev];
          next[i] = true;
          return next;
        });
      }, delayBase * i);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);
  return visibleItems;
}

export default function AboutStory() {
  const milestones = [
    { year: '2023', title: 'Founded in Cyprus', description: 'CJ Solutions Ltd. was established in Limassol, Cyprus, with a clear mission: to simplify compliance and corporate advisory for regulated businesses across the globe.' },
    { year: '2023', title: 'First Clients Onboarded', description: 'Within months of launch, we onboarded our first clients in the forex and iGaming sectors, delivering AML frameworks and compliance outsourcing solutions.' },
    { year: '2024', title: 'License Marketplace Launched', description: 'We expanded into the secondary license market, connecting verified sellers of already-issued forex and iGaming licenses with qualified buyers worldwide.' },
    { year: '2024', title: 'Regional Expansion', description: 'Our advisory reach grew to cover jurisdictions across Seychelles, Vanuatu, Mauritius, Comoros, Kenya, Curaçao, Anjouan, Liberia, and Costa Rica.' },
    { year: '2025', title: 'Full-Service Consulting Firm', description: 'Today, CJ Solutions is a full-service corporate consulting firm offering licensing, compliance, legal drafting, accounting, tax, payroll, and company formation services.' },
  ];

  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation();
  const { ref: timelineRef, isVisible: timelineVisible } = useScrollAnimation();
  const staggered = useStaggerVisible(milestones.length, timelineVisible, 150);

  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left: Text */}
          <div
            ref={leftRef as React.RefObject<HTMLDivElement>}
            className={`transition-all duration-700 ${leftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
          >
            <span className="inline-block px-3 py-1 bg-amber-50 border border-amber-200 text-amber-600 text-sm font-semibold uppercase tracking-widest rounded mb-5">
              Our Story
            </span>
            <h2 className="text-4xl sm:text-5xl font-semibold text-slate-900 leading-tight mb-6">
              How CJ Solutions Came to Be
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-5">
              CJ Solutions Ltd. was founded in 2023 in Limassol, Cyprus one of Europe's most dynamic financial and regulatory hubs. The company was born out of a simple observation: businesses entering regulated industries were spending years and significant capital trying to obtain licenses from scratch, when in many cases, already-issued licenses were available for acquisition.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed mb-5">
              Our founders seasoned compliance and corporate advisory professionals saw an opportunity to bridge that gap. We built a trusted marketplace where verified license holders can connect with qualified buyers, making the process faster, safer, and more transparent.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed">
              Beyond licensing, we grew into a full-service consulting firm, supporting clients with AML/KYC frameworks, compliance outsourcing, legal drafting, accounting, tax, payroll, and company formation all under one roof.
            </p>
          </div>

          {/* Right: Timeline */}
          <div
            ref={timelineRef as React.RefObject<HTMLDivElement>}
            className="relative"
          >
            <div className="absolute left-5 top-0 bottom-0 w-px bg-amber-200"></div>
            <div className="space-y-10">
              {milestones.map((m, i) => (
                <div
                  key={i}
                  className={`relative pl-14 transition-all duration-600 ${staggered[i] ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
                  style={{ transitionDuration: '600ms' }}
                >
                  <div className="absolute left-0 top-1 w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center shadow-md">
                    <span className="text-white text-xs font-bold">{m.year.slice(2)}</span>
                  </div>
                  <span className="text-amber-500 text-sm font-semibold uppercase tracking-widest">{m.year}</span>
                  <h3 className="text-xl font-semibold text-slate-900 mt-1 mb-2">{m.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{m.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
